import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { sacredPath } from "@/lib/sacred-library/utils/registry";
import { searchSacredLibrary } from "@/lib/sacred-library/utils/search";
import { SacredIcon } from "./SacredIcon";
import { SACRED_FALLBACK_IMAGE } from "@/lib/sacred-library/utils/image-urls";
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

export function SacredSearchBar({ autoFocus = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const mergeResults = useCallback((apiHits, clientHits) => {
    const seen = new Set();
    const merged = [];
    for (const h of [...apiHits, ...clientHits]) {
      const key = `${h.type}:${h.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(h);
    }
    return merged.slice(0, 20);
  }, []);

  const runSearch = useCallback(
    async (query) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setResults([]);
        return;
      }
      setLoading(true);
      const clientHits = searchSacredLibrary(trimmed, 20);
      try {
        const res = await axios.get(`${API}/library/search`, { params: { q: trimmed, limit: 20 } });
        setResults(mergeResults(res.data?.results ?? [], clientHits));
      } catch {
        setResults(clientHits);
      }
      setLoading(false);
    },
    [mergeResults]
  );

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 280);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  return (
    <div className="relative max-w-xl mx-auto" data-testid="sacred-library-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search deities, festivals, vratas, stories, sādhanas…"
          className="pl-10 rounded-full"
          autoFocus={autoFocus}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
      </div>
      {results.length > 0 && (
        <ul className="absolute z-20 mt-2 w-full rounded-lg border bg-white shadow-float max-h-72 overflow-auto">
          {results.map((hit) => (
            <li key={`${hit.type}-${hit.id}`}>
              <Link
                to={sacredPath(hit.type, hit.id)}
                className="flex gap-3 px-4 py-2.5 hover:bg-muted/50 text-sm items-center"
                onClick={() => setQ("")}
              >
                <SacredIcon src={hit.image_url || SACRED_FALLBACK_IMAGE} alt={hit.name} className="w-9 h-9 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{hit.name}</span>
                  <span className="text-xs text-muted-foreground ml-2 capitalize">{hit.type}</span>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{hit.summary}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
